/*
  LFY — enable the two-column Description | Specifications layout.
  New file: assets/lfy-two-col-tabs.js  (load with defer from theme.liquid or the PDP section)

  Why JS at all: the CSS needs (a) a hook class on the container, and (b) each panel's
  label available for the ::before heading, because the tab strip is hidden in this
  layout. Liquid could add the class, but the tab titles are authored as theme-editor
  block settings, so reading them from the rendered strip is more robust than
  duplicating the strings.

  Deliberately conservative:
   - Only activates when BOTH a description panel and our Specifications panel exist.
     A product with no spec metafield keeps the normal single-tab layout.
   - Only >=1025px. Below that it removes the hook so the theme's own accordion/tab
     behaviour is completely untouched (mobile and tablet keep working as today).
   - Never removes or reorders DOM. It only adds a class and a data attribute, so the
     theme's own tab JS (tabs.js) keeps functioning if the layout is switched off.
*/
(function () {
  var MQ = '(min-width: 1025px)';

  function labelFor(id, strip) {
    if (!strip) return '';
    var link = strip.querySelector('a.tab-title[href="#' + id + '"]');
    return link ? link.textContent.trim() : '';
  }

  // Turns one tab panel into a collapsible card.
  //   openByDefault - start expanded
  //   linkA/linkB   - when both given and this panel is one of them, toggling one
  //                   toggles the other (the grid's top row must move together,
  //                   or a collapsed 83px bar sits beside a tall panel)
  function bindCollapsible(panel, openByDefault, linkA, linkB) {
    var kids = Array.prototype.slice.call(panel.children);
    var title = kids.filter(function (k) { return k.classList.contains('toggle-title'); })[0];
    var body = kids.filter(function (k) { return k.classList.contains('toggle-content'); })[0];
    if (!title || !body) return;

    panel.classList.add('lfy-collapsible');
    if (panel.getAttribute('data-lfy-bound') === '1') return;
    panel.setAttribute('data-lfy-bound', '1');

    if (openByDefault) panel.classList.add('is-open');
    title.setAttribute('role', 'button');
    title.setAttribute('tabindex', '0');
    title.setAttribute('aria-expanded', openByDefault ? 'true' : 'false');

    function setOpen(el, open) {
      if (!el) return;
      el.classList.toggle('is-open', open);
      var t = Array.prototype.slice.call(el.children).filter(function (k) {
        return k.classList.contains('toggle-title');
      })[0];
      if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function toggle() {
      var open = !panel.classList.contains('is-open');
      setOpen(panel, open);
      if (linkA && linkB) {
        if (panel === linkA) setOpen(linkB, open);
        else if (panel === linkB) setOpen(linkA, open);
      }
    }

    // The theme's tabs.js binds its own `tabToggle` to every `a.toggleLink` inside
    // the header (assets/tabs.js). A click therefore hit BOTH handlers and they
    // fought each other — the panel needed two clicks, or none at all below
    // 1280px. Listening in the CAPTURE phase on the header lets us stop the
    // event before it ever reaches the theme's listener on the inner anchor.
    title.addEventListener('click', function (e) {
      // real outbound links inside a panel header still work
      if (e.target.closest && e.target.closest('a[href^="http"]')) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      toggle();
    }, true);

    // Anchors default to following their href on Enter; capture that too.
    title.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        toggle();
      }
    }, true);
  }

  function apply(root) {
    // OPT-IN GATE: only products assigned one of our theme templates carry a flag
    // (set in halo-product-tab.liquid from template.suffix). Without this, the
    // layout would change all ~8,900 product pages at once.
    //   two-col-tabs -> grid: Description | Specifications, full width below
    //   two-col-wide -> stacked: all four collapsed inside the buy column
    var isGrid = root.getAttribute('data-lfy-two-col') === 'true';
    var isStacked = root.getAttribute('data-lfy-stacked') === 'true';
    if (!isGrid && !isStacked) return;

    var contents = root.querySelector('.tabs-contents');
    if (!contents) return;

    var desc = contents.querySelector('#tab-product-description');
    var spec = contents.querySelector('#tab-product-specifications');

    // STACKED variant: every panel collapsed, no grid. The buy column is ~898px,
    // so panels stack; leaving Description open there would push the product
    // images far above the fold.
    if (isStacked) {
      // NO breakpoint gate: the collapsible cards are the primary interaction on
      // this template, so they behave identically on mobile, tablet, desktop and
      // widescreen. Only the two-column arrangement is breakpoint-dependent, and
      // that lives in CSS. A different mechanism per screen size is harder to use
      // and harder to reason about.
      contents.classList.add('lfy-cards');
      Array.prototype.forEach.call(contents.children, function (panel) {
        if (!panel.querySelector) return;
        bindCollapsible(panel, false, null, null);
      });
      return;
    }

    // Both panels must exist, and the spec panel must actually have content —
    // an empty two-column grid looks broken, worse than a single column.
    var specHasContent = spec && spec.textContent.trim().length > 0;
    if (!desc || !spec || !specHasContent) {
      contents.classList.remove('lfy-2col');
      root.classList.remove('lfy-2col-active');
      return;
    }

    if (!window.matchMedia(MQ).matches) {
      contents.classList.remove('lfy-2col');
      root.classList.remove('lfy-2col-active');
      return;
    }

    var strip = root.querySelector('.tabs.tabs-horizontal') || root.querySelector('.tabs-horizontal');
    Array.prototype.forEach.call(contents.children, function (panel) {
      if (!panel.id) return;
      if (!panel.getAttribute('data-tab-label')) {
        var l = labelFor(panel.id, strip);
        if (l) panel.setAttribute('data-tab-label', l);
      }
    });

    contents.classList.add('lfy-2col');
    root.classList.add('lfy-2col-active');

    // ALL FOUR panels become collapsible accordions so the interaction is
    // consistent across the block. Description and Specifications start OPEN
    // (they are the primary content); Datasheet and Warranties start CLOSED —
    // measured at 3,061px and 1,841px on HV3112T, taller than the description,
    // and Warranties is 5,250 chars of boilerplate repeated on every product.
    //
    // The markup is already a header/body pair (.toggle-title + .toggle-content),
    // so this only adds classes and ARIA; no DOM is moved or rewritten.
    Array.prototype.forEach.call(contents.children, function (panel) {
      if (!panel.querySelector) return;
      bindCollapsible(panel, panel === desc || panel === spec, desc, spec);
    });
  }

  function run() {
    var roots = document.querySelectorAll('product-tab.productView-tab, .productView-tab');
    Array.prototype.forEach.call(roots, apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-evaluate on resize so crossing the breakpoint hands control back to the
  // theme's tab behaviour cleanly.
  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(run, 150);
  });

  // The tab markup is injected client-side, so it may not exist at DOMContentLoaded.
  // Watch briefly for it rather than polling forever.
  if (window.MutationObserver) {
    var tries = 0;
    var mo = new MutationObserver(function () {
      run();
      if (++tries > 40) mo.disconnect();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 8000);
  }
})();
